/**
 * US-176 Phase 2 — the lock-screen's "pick your profile" list, resolved
 * entirely from THIS DEVICE's own local state, never a server query.
 *
 * The spec's original wording ("staff picks their name from the on-shift
 * roster") implied a server-side roster lookup — but that would mean an
 * UNAUTHENTICATED endpoint revealing which employees are on shift at a
 * facility, reachable by anyone with physical access to an idle unlocked
 * cart. That is new PII exposure this feature does not need: the set of
 * employees who could plausibly use fast-reauth on this specific device is
 * already exactly "whoever has minted a credential here," which is local
 * state only this device holds. Storing it in localStorage (not Keychain)
 * is deliberate — email + display name are not secrets, and this cache's
 * only job is populating a picker; the actual credential stays in the
 * platform-specific secure store (Keychain / desktop's future safeStorage
 * equivalent).
 */
export interface ReauthProfile {
  email: string;
  displayName: string;
}

const PROFILE_CACHE_KEY = "ls.reauthProfiles";

function readAll(): ReauthProfile[] {
  try {
    const raw = localStorage.getItem(PROFILE_CACHE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (p): p is ReauthProfile =>
        typeof p === "object" &&
        p !== null &&
        typeof (p as ReauthProfile).email === "string" &&
        typeof (p as ReauthProfile).displayName === "string",
    );
  } catch {
    return [];
  }
}

function writeAll(profiles: ReauthProfile[]): void {
  try {
    localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify(profiles));
  } catch {
    // Storage unavailable — best-effort; the picker just shows nothing and
    // the caller falls back to the plain email+password form.
  }
}

export function listReauthProfiles(): ReauthProfile[] {
  return readAll();
}

/** Called after a credential is successfully minted+registered (a full
 * sign-in just completed on this device). REPLACES the entire cached list
 * with just this one profile — never appends/upserts into a growing array
 * (security review F2, 2026-09-17). The device's re-auth keypair is
 * single-slot (mint always replaces any prior key, mirroring iOS's
 * SecItemDelete-before-mint), so at most one cached profile can ever
 * actually complete a PIN sign-in at a time; a multi-entry list let an
 * earlier employee's picker entry survive on-screen after a second
 * employee's sign-in had already invalidated the key backing it, dead-
 * ending in a generic "Incorrect PIN" with no way to tell why. Replacing
 * keeps the picker's contents truthful to what the device can actually do
 * right now. */
export function addReauthProfile(profile: ReauthProfile): void {
  writeAll([profile]);
}

export function removeReauthProfile(email: string): void {
  writeAll(readAll().filter((p) => p.email !== email));
}

export function hasReauthProfile(email: string): boolean {
  return readAll().some((p) => p.email === email);
}

/** Called on a hard sign-out (8h ceiling) — the device credential is
 * cleared at the same moment, so the cached profile that pointed at it
 * must not survive either (a stale entry would let the picker offer a PIN
 * flow that can only fail). */
export function clearAllReauthProfiles(): void {
  try {
    localStorage.removeItem(PROFILE_CACHE_KEY);
  } catch {
    // Storage unavailable — non-fatal.
  }
}
